import React, { useRef, useEffect, useState, useContext } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { ThemeContext } from '../contexts/ThemeContext';
import { createLogger } from '../utils/debugLogger';
import {
  Chart,
  LineElement,
  PointElement, 
  LinearScale, 
  TimeScale, 
  Tooltip, 
  Legend,
  CategoryScale,
  LineController,
  BarElement,
  BarController,
  Filler
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import 'chartjs-adapter-date-fns'; // Required for time scale
import { getAccountHistory, getAggregatedAccountHistory, getAllAccounts } from '../services/api';

// Create debug logger for this module
const logger = createLogger('balanceChart', 'Balance history chart and portfolio analytics', false, 'INFO');

// Register Chart.js components
Chart.register(
  LineElement, 
  PointElement, 
  LinearScale, 
  TimeScale, 
  Tooltip, 
  Legend,
  CategoryScale,
  LineController,
  BarElement,
  BarController,
  Filler,
  ChartDataLabels
);

// Custom plugin for trophy icons at all-time highs
const createTrophyPlugin = (allTimeHighs) => ({
  id: 'trophyPlugin',
  afterDraw: (chart, args, options) => {
    logger.debug('🎯 TROPHY PLUGIN EXECUTED!');
    logger.debug('AllTimeHighs passed to plugin:', allTimeHighs);
    
    const { ctx, data, scales } = chart;
    const { x, y } = scales;
    
    if (!allTimeHighs || !allTimeHighs.length) {
      logger.debug('❌ No allTimeHighs found');
      return;
    }

    logger.debug('✅ Trophy plugin running, allTimeHighs:', allTimeHighs);
    logger.debug('Data length:', data.datasets[0].data.length);
    logger.debug('Labels length:', data.labels.length);
    
    ctx.save();
    
    // Helper function to draw a star
    const drawStar = (centerX, centerY, starSize, fillColor, strokeColor, lineWidth = 1) => {
      ctx.fillStyle = fillColor;
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = lineWidth;
      
      ctx.beginPath();
      for (let i = 0; i < 10; i++) {
        const angle = (i * Math.PI) / 5 - Math.PI / 2;
        const radius = i % 2 === 0 ? starSize : starSize * 0.5;
        const x = centerX + radius * Math.cos(angle);
        const y = centerY + radius * Math.sin(angle);
        
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    };
    
    // Draw all historic all-time highs with muted color stars
    allTimeHighs.forEach((allTimeHighIndex, index) => {
      // Validate that the index is within bounds
      if (allTimeHighIndex >= data.datasets[0].data.length || allTimeHighIndex >= data.labels.length) {
        logger.debug(`❌ Skipping invalid index ${allTimeHighIndex} - out of bounds`);
        return;
      }
      
      const dataPoint = data.datasets[0].data[allTimeHighIndex];
      const label = data.labels[allTimeHighIndex];
      
      logger.debug(`🔍 Processing all-time high at index ${allTimeHighIndex}:`);
      logger.debug(`  - Data point: ${dataPoint}`);
      logger.debug(`  - Label: ${label}`);
      logger.debug(`  - Is null/undefined: ${dataPoint === null || dataPoint === undefined}`);
      
      if (dataPoint !== null && dataPoint !== undefined && label !== null && label !== undefined) {
        const xPos = x.getPixelForValue(label);
        const yPos = y.getPixelForValue(dataPoint);
        
        const isLastAllTimeHigh = index === allTimeHighs.length - 1;
        
        logger.debug(`  - X position: ${xPos}, Y position: ${yPos}`);
        logger.debug(`  - Is last all-time high: ${isLastAllTimeHigh}`);
        
        if (isLastAllTimeHigh) {
          // Draw prominent golden star for current all-time high
          logger.debug('🌟 Drawing GOLDEN star for current all-time high');
          drawStar(xPos, yPos - 15, 10.5, '#FFD700', '#FF8C00', 1);
        } else {
          // Draw smaller muted star for historic all-time highs
          logger.debug('⭐ Drawing muted star for historic all-time high');
          drawStar(xPos, yPos - 12, 6, '#B0B0B0', '#808080', 0.5);
        }
      } else {
        logger.debug(`❌ Skipping index ${allTimeHighIndex} - data point or label is null/undefined`);
      }
    });
    
    ctx.restore();
  }
});

function BalanceHistoryModal({
  show,
  onHide,
  accountId,
  accountName,
  portfolioName,
  isAggregated = false,
  currentBalance = null,
  isBalanceLoading = false
}) {
  const balanceChartRef = useRef(null);
  const dailyGainChartRef = useRef(null);
  const balanceChartInstance = useRef(null);
  const dailyGainChartInstance = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [historyData, setHistoryData] = useState([]);
  const [periodGainLoss, setPeriodGainLoss] = useState({ amount: 0, percentage: 0 });
  const [todayGainLoss, setTodayGainLoss] = useState({ amount: 0, percentage: 0 });
  const [allTimeHighDistance, setAllTimeHighDistance] = useState({ percentage: 0, amount: 0 });
  const [allTimeHighs, setAllTimeHighs] = useState([]);
  // Always follow app theme
  const { effectiveTheme: chartTheme } = useContext(ThemeContext);
  const periodStorageKey = 'balanceHistoryPeriod';
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    try {
      const saved = localStorage.getItem(periodStorageKey);
      return saved || 'ytd';
    } catch (error) {
      return 'ytd';
    }
  }); // 'ytd', '1month', '3months', '6months', '1year', '3years', 'alltime'
  const [forceRefetch, setForceRefetch] = useState(0); // Force refetch when period changes

  // No manual theme override; follows app theme

  const combinedLoading = loading || isBalanceLoading;
  const loadingMessage = loading
    ? 'Loading balance history data...'
    : isBalanceLoading
      ? 'Loading live balance data...'
      : 'Loading...';

  useEffect(() => {
    if (!show || combinedLoading) {
      if (balanceChartInstance.current) {
        balanceChartInstance.current.destroy();
        balanceChartInstance.current = null;
      }
      if (dailyGainChartInstance.current) {
        dailyGainChartInstance.current.destroy();
        dailyGainChartInstance.current = null;
      }
    }
  }, [combinedLoading, show]);

  useEffect(() => {
    if (!selectedPeriod) {
      return;
    }
    try {
      localStorage.setItem(periodStorageKey, selectedPeriod);
    } catch (error) {
      // Ignore storage errors (private mode, etc.)
    }
  }, [selectedPeriod, periodStorageKey]);

  // Fetch account history data when modal is shown
  useEffect(() => {
    if (!show) {
      // Reset state when modal is closed
      setHistoryData([]);
      setPeriodGainLoss({ amount: 0, percentage: 0 });
      setTodayGainLoss({ amount: 0, percentage: 0 });
      setAllTimeHighDistance({ percentage: 0, amount: 0 });
      setAllTimeHighs([]);
      return;
    }
    if (!isAggregated && !accountId) return;
    
    const fetchHistoryData = async () => {
      try {
        setLoading(true);
        setError('');
        
        logger.debug('Fetching history data with period:', selectedPeriod);
        // Fetch account-specific or aggregated history based on isAggregated flag
        const response = isAggregated 
          ? await getAggregatedAccountHistory({ period: selectedPeriod })
          : await getAccountHistory(accountId, { period: selectedPeriod });
          
        const history = response.data.history || [];
        // Filter out Saturdays (6) and Sundays (0) for both account-specific and aggregated views
        const filteredHistory = history.filter(item => {
          // Parse date carefully to avoid timezone shifts
          const dateParts = item.date.split('-');
          const year = parseInt(dateParts[0]);
          const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
          const day = parseInt(dateParts[2]);
          const localDate = new Date(year, month, day);
          const dayOfWeek = localDate.getDay();
          return dayOfWeek !== 0 && dayOfWeek !== 6;
        });
        setHistoryData(filteredHistory);
        
        // Calculate all-time highs
        const allTimeHighIndices = [];
        let currentMax = -Infinity;
        
        filteredHistory.forEach((item, index) => {
          if (item.total_value > currentMax) {
            currentMax = item.total_value;
            allTimeHighIndices.push(index);
          }
        });
        
        // Check if current balance is a new all-time high
        if (currentBalance !== null && currentBalance > currentMax) {
          // Current balance is higher than any historical value
          // We'll add it to the chart data and mark it as all-time high
          // The index will be the last position in the chart data
          allTimeHighIndices.push(filteredHistory.length); // This will be the last index when current balance is added
        }
        
        // console.log('All-time highs calculated:', allTimeHighIndices, 'from history length:', history.length);
        setAllTimeHighs(allTimeHighIndices);
        
        // Calculate daily percentage changes
        const gainData = [];
        
        if (filteredHistory.length > 1) {
          for (let i = 1; i < filteredHistory.length; i++) {
            const prevValue = filteredHistory[i-1].total_value;
            const currentValue = filteredHistory[i].total_value;
            
            if (prevValue && prevValue > 0) {
              const percentChange = ((currentValue - prevValue) / prevValue) * 100;
              gainData.push({
                date: filteredHistory[i].date,
                percentChange: parseFloat(percentChange.toFixed(2))
              });
            } else {
              gainData.push({
                date: filteredHistory[i].date,
                percentChange: 0
              });
            }
          }
        }
        
        
        // Note: Period gain/loss calculation moved to separate useEffect to include currentBalance
        
      } catch (err) {
        logger.error('Error fetching account history:', err);
        setError(`Failed to load ${isAggregated ? 'aggregated' : 'account'} history data`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchHistoryData();
    // currentBalance/selectedPeriod intentionally omitted: currentBalance streams from
    // live price updates and would trigger a full history refetch on every tick; period
    // changes are handled by the dedicated effect below via forceRefetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, accountId, isAggregated, forceRefetch]);

  // Force chart re-render when theme changes
  useEffect(() => {
    if (show) {
      // This will trigger a re-render of the charts with new theme
      setHistoryData(h => h.length > 0 ? [...h] : h);
    }
  }, [chartTheme, show]);

  // Reload data when period changes
  useEffect(() => {
    if (show && selectedPeriod) {
      logger.debug('Period changed to:', selectedPeriod, '- triggering refetch');
      // Reset data to trigger reload
      setHistoryData([]);
      setLoading(true);
      // Force a refetch by updating a dependency
      setForceRefetch(prev => prev + 1);
    }
    // 'show' intentionally omitted: opening the modal already triggers a fetch via the
    // effect above (which depends on `show`); including it here would double-fetch on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod]);

  // Clean up chart instances when component unmounts
  useEffect(() => {
    return () => {
      if (balanceChartInstance.current) {
        balanceChartInstance.current.destroy();
        balanceChartInstance.current = null;
      }
      if (dailyGainChartInstance.current) {
        dailyGainChartInstance.current.destroy();
        dailyGainChartInstance.current = null;
      }
    };
  }, []);

  // Fetch YTD data from /api/accounts/all for accurate YTD calculation
  useEffect(() => {
    if (!show || !isAggregated || selectedPeriod !== 'ytd') return;

    const fetchYtdData = async () => {
      try {
        const response = await getAllAccounts();
        const accounts = response.data || [];

        // Calculate total YTD gain from accounts (same as accounts page)
        const totalYtdGain = accounts.reduce((sum, account) => sum + (account.ytd_gain_amount || 0), 0);
        const totalYtdStartingBalance = accounts.reduce((sum, account) => {
          const currentBalance = account.balance || 0;
          const ytdGain = account.ytd_gain_amount || 0;
          return sum + (currentBalance - ytdGain);
        }, 0);

        const ytdPercentage = totalYtdStartingBalance > 0 ? (totalYtdGain / totalYtdStartingBalance) * 100 : 0;

        logger.debug('YTD from /api/accounts/all:', { totalYtdGain, totalYtdStartingBalance, ytdPercentage });

        setPeriodGainLoss({
          amount: totalYtdGain,
          percentage: ytdPercentage
        });
      } catch (err) {
        logger.error('Error fetching YTD data from /api/accounts/all:', err);
      }
    };

    fetchYtdData();
  }, [show, isAggregated, selectedPeriod]);

  // Calculate period gain/loss and today's gain including current balance
  useEffect(() => {
    if (!historyData || historyData.length === 0) {
      setPeriodGainLoss({ amount: 0, percentage: 0 });
      setTodayGainLoss({ amount: 0, percentage: 0 });
      return;
    }

    // For YTD period with aggregated data, skip calculation (use data from /api/accounts/all)
    if (selectedPeriod === 'ytd' && isAggregated) {
      logger.debug('Skipping calculation for YTD - using data from /api/accounts/all');
      // Don't override periodGainLoss - it's set by the effect above
    } else {
      // Calculate period gain/loss for non-YTD periods
      const firstValue = historyData[0].total_value;
      // Use current balance if provided, otherwise use last historical value
      const lastValue = currentBalance !== null ? currentBalance :
                        (historyData.length >= 2 ? historyData[historyData.length - 1].total_value : firstValue);
      const gainAmount = lastValue - firstValue;
      const gainPercentage = firstValue > 0 ? (gainAmount / firstValue) * 100 : 0;

      setPeriodGainLoss({
        amount: gainAmount,
        percentage: gainPercentage
      });
    }

    // Calculate today's gain (yesterday to today)
    if (historyData.length >= 2) {
      const todayValue = currentBalance !== null ? currentBalance : historyData[historyData.length - 1].total_value;
      // Find yesterday's value (second to last entry or look back for previous day)
      let yesterdayValue = historyData[historyData.length - 2].total_value;
      
      // If we have current balance and today's historical data exists, use previous day
      const today = new Date();
      const lastHistoricalDate = new Date(historyData[historyData.length - 1].date);
      const isLastEntryToday = lastHistoricalDate.toDateString() === today.toDateString();
      
      if (isLastEntryToday && historyData.length >= 2) {
        yesterdayValue = historyData[historyData.length - 2].total_value;
      } else if (historyData.length >= 2) {
        yesterdayValue = historyData[historyData.length - 2].total_value;
      }
      
      const todayGainAmount = todayValue - yesterdayValue;
      const todayGainPercentage = yesterdayValue > 0 ? (todayGainAmount / yesterdayValue) * 100 : 0;
      
      setTodayGainLoss({
        amount: todayGainAmount,
        percentage: todayGainPercentage
      });
    } else {
      setTodayGainLoss({ amount: 0, percentage: 0 });
    }

    // Calculate all-time high distance
    if (historyData.length > 0) {
      const currentValue = currentBalance !== null ? currentBalance : historyData[historyData.length - 1].total_value;
      
      // Find the all-time high value from the historical data
      const allTimeHighValue = Math.max(...historyData.map(item => item.total_value));
      
      // Also consider current balance if it's higher
      const actualAllTimeHigh = currentBalance !== null ? Math.max(allTimeHighValue, currentBalance) : allTimeHighValue;
      
      // Calculate distance as percentage and dollar amount
      let distancePercentage = 0;
      let distanceAmount = 0;
      if (actualAllTimeHigh > 0) {
        distanceAmount = currentValue - actualAllTimeHigh;
        distancePercentage = (distanceAmount / actualAllTimeHigh) * 100;
      }
      
      setAllTimeHighDistance({ 
        percentage: distancePercentage, 
        amount: distanceAmount 
      });
    } else {
      setAllTimeHighDistance({ percentage: 0, amount: 0 });
    }
  }, [historyData, currentBalance, selectedPeriod, isAggregated]);

  // Create or update charts when data is loaded
  // Include current balance and theme dependencies so late-arriving values
  // (e.g. ticker data still loading when the modal opens) retrigger a chart
  // render instead of showing a misleading drop.
  useEffect(() => {
    if (!show || !balanceChartRef.current || !dailyGainChartRef.current || combinedLoading || historyData.length === 0) return;

    // Destroy previous chart instances if they exist
    if (balanceChartInstance.current) {
      balanceChartInstance.current.destroy();
    }
    if (dailyGainChartInstance.current) {
      dailyGainChartInstance.current.destroy();
    }

    // Prepare data for the charts
    let dates = historyData.map(item => {
      // Parse date carefully to avoid timezone shifts
      const dateParts = item.date.split('-');
      const year = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
      const day = parseInt(dateParts[2]);
      return new Date(year, month, day);
    });
    let values = historyData.map(item => item.total_value);
    
    // Add or update current real-time balance as today's data point if provided
    let currentBalanceAdded = false;
    if (currentBalance !== null && historyData.length > 0) {
      const lastHistoricalValue = historyData[historyData.length - 1]?.total_value;
      const today = new Date();
      const todayDay = today.getDay();
      const lastHistoricalDate = dates[dates.length - 1];
      
      // Only add/update current balance if today is not a weekend
      if (todayDay !== 0 && todayDay !== 6) {
        // Check if the last historical entry is from today
        const isLastEntryToday = lastHistoricalDate && lastHistoricalDate.toDateString() === today.toDateString();
        
        if (isLastEntryToday) {
          // Replace today's historical value with current balance if different
          if (Math.abs(currentBalance - lastHistoricalValue) > 0.01) {
            values[values.length - 1] = currentBalance;
            currentBalanceAdded = true;
          }
        } else {
          // Add today's data point since there's no historical entry for today
          dates.push(today);
          values.push(currentBalance);
          currentBalanceAdded = true;
        }
      }
    }
    
    // Recalculate all-time highs after adding current balance
    let finalAllTimeHighs = [...allTimeHighs];
    if (currentBalance !== null) {
      // Use the actual chart values (values array) instead of recalculating
      const allTimeHighIndices = [];
      let currentMax = -Infinity;
      
      values.forEach((value, index) => {
        if (value > currentMax) {
          currentMax = value;
          allTimeHighIndices.push(index);
        }
      });
      
      finalAllTimeHighs = allTimeHighIndices;
      logger.debug('🔍 DEBUG: Recalculated all-time highs:', finalAllTimeHighs);
      logger.debug('🔍 DEBUG: Chart values:', values);
      logger.debug('🔍 DEBUG: Current balance:', currentBalance);
      logger.debug('🔍 DEBUG: Current balance added:', currentBalanceAdded);
      logger.debug('🔍 DEBUG: Last all-time high index:', finalAllTimeHighs[finalAllTimeHighs.length - 1]);
      logger.debug('🔍 DEBUG: Last all-time high value:', values[finalAllTimeHighs[finalAllTimeHighs.length - 1]]);
      logger.debug('🔍 DEBUG: Is current balance the last all-time high?', finalAllTimeHighs[finalAllTimeHighs.length - 1] === values.length - 1);
    }
    
    // Determine appropriate time unit based on data range
    let timeUnit = 'month';
    if (dates.length > 0) {
      const firstDate = new Date(dates[0]);
      const lastDate = new Date(dates[dates.length - 1]);
      const daysDiff = Math.ceil((lastDate - firstDate) / (1000 * 60 * 60 * 24));
      
      if (daysDiff <= 14) {
        timeUnit = 'day';
      } else if (daysDiff <= 60) {
        timeUnit = 'week';
      } else {
        timeUnit = 'month';
      }
    }
    
    // Ensure min/max dates are the same for both charts for perfect alignment
    const minDate = dates.length > 0 ? dates[0] : undefined;
    // Add a small buffer (1 day) to the max date to prevent cutoff
    const maxDate = dates.length > 0 ? 
      new Date(dates[dates.length - 1].getTime() + 86400000) : undefined;
    
    // Common x-axis configuration for perfect alignment
    const commonXConfig = {
      type: 'time',
      time: {
        unit: timeUnit,
        displayFormats: {
          day: 'MMM d',
          week: 'MMM d',
          month: 'MMM yyyy'
        },
        tooltipFormat: 'PPP'
      },
      min: minDate,
      max: maxDate,
      offset: false,
      grid: {
        color: chartTheme === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)'
      },
      ticks: {
        maxRotation: 0,
        autoSkip: true,
        color: chartTheme === 'light' ? '#212529' : '#f8f9fa'
      },
      // Add padding to prevent cutoff
      afterFit: function(scaleInstance) {
        scaleInstance.paddingRight = 20; // Add padding to the right side
      }
    };

    // Balance chart (TOP)
    try {
      const balanceCtx = balanceChartRef.current.getContext('2d');
      
      // console.log('Creating balance chart with allTimeHighs:', allTimeHighs);
      
      balanceChartInstance.current = new Chart(balanceCtx, {
        type: 'line',
        data: {
          labels: dates,
          datasets: [
            {
              label: isAggregated ? 'Total Portfolio Balance' : 'Account Balance',
              data: values,
              borderColor: 'rgb(75, 192, 192)',
              backgroundColor: 'rgba(75, 192, 192, 0.2)',
              tension: 0.1,
              fill: true,
              pointRadius: 3,
              pointHoverRadius: 6,
              pointBackgroundColor: 'rgb(75, 192, 192)',
            },
          ],
        },
        plugins: [createTrophyPlugin(finalAllTimeHighs)],
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              top: 25, // Add top padding for star
              right: 15 // Add extra padding to the right
            }
          },
          plugins: {
            legend: {
              display: false, // No legend for balance chart
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              backgroundColor: chartTheme === 'light' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(33, 37, 41, 0.95)',
              titleColor: chartTheme === 'light' ? '#212529' : '#f8f9fa',
              bodyColor: chartTheme === 'light' ? '#212529' : '#f8f9fa',
              borderColor: chartTheme === 'light' ? '#dee2e6' : '#495057',
              borderWidth: 1,
              callbacks: {
                label: (context) => {
                  const value = context.raw || 0;
                  const isAllTimeHigh = finalAllTimeHighs.includes(context.dataIndex);
                  const isCurrentBalance = context.dataIndex === values.length - 1;
                  const isCurrentAllTimeHigh = isCurrentBalance && isAllTimeHigh;
                  
                  let label = `Balance: $${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                  if (isCurrentAllTimeHigh) {
                    label += ' ⭐ (Current All-Time High!)';
                  } else if (isAllTimeHigh) {
                    label += ' 🏆 (All-Time High)';
                  }
                  return label;
                }
              }
            },
            datalabels: {
              display: false // No labels for balance chart
            }
          },
          scales: {
            x: {
              ...commonXConfig,
              // Hide bottom axis for top chart
              display: false
            },
            y: {
              title: {
                display: true,
                text: isAggregated ? 'Total Portfolio Balance ($)' : 'Account Balance ($)',
                color: chartTheme === 'light' ? '#212529' : '#f8f9fa'
              },
              ticks: {
                color: chartTheme === 'light' ? '#212529' : '#f8f9fa',
                callback: (value) => {
                  return '$' + value.toLocaleString('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  });
                }
              },
              grid: {
                color: chartTheme === 'light' ? 'rgba(0, 0, 0, 0.25)' : 'rgba(255, 255, 255, 0.25)',
                lineWidth: 1.5
              }
            }
          }
        }
              });
      } catch (balanceChartError) {
      logger.error('Error creating balance chart:', balanceChartError);
      setError('Failed to create balance history chart');
    }
    
    // Daily gain chart (BOTTOM)
    try {
      const gainCtx = dailyGainChartRef.current.getContext('2d');
      
      // Prepare gain data aligned with balance chart
      const gainValues = new Array(dates.length).fill(null);
      
      // Calculate gain values for historical data points
      for (let i = 1; i < historyData.length; i++) {
        const prevValue = historyData[i-1].total_value;
        const currentValue = historyData[i].total_value;
        
        if (prevValue && prevValue > 0) {
          const percentChange = ((currentValue - prevValue) / prevValue) * 100;
          gainValues[i] = parseFloat(percentChange.toFixed(2));
        } else {
          gainValues[i] = 0;
        }
      }
      
      // Handle the case where current balance was added as an extra data point
      if (dates.length > historyData.length && historyData.length > 0) {
        // Calculate gain from last historical value to current balance
        const lastHistoricalValue = historyData[historyData.length - 1].total_value;
        if (currentBalance !== null && lastHistoricalValue > 0) {
          const percentChange = ((currentBalance - lastHistoricalValue) / lastHistoricalValue) * 100;
          gainValues[historyData.length] = parseFloat(percentChange.toFixed(2));
        }
      }
      // Handle the case where today's value was replaced (dates.length === historyData.length)
      else if (currentBalance !== null && historyData.length > 1 && dates.length === historyData.length) {
        const today = new Date();
        const todayDay = today.getDay();
        const lastHistoricalDate = dates[dates.length - 1];
        const isLastEntryToday = lastHistoricalDate && lastHistoricalDate.toDateString() === today.toDateString();
        
        // Only recalculate if today is not a weekend
        if (isLastEntryToday && todayDay !== 0 && todayDay !== 6) {
          // Recalculate the last gain value using the updated current balance
          const prevValue = historyData[historyData.length - 2].total_value;
          if (prevValue > 0) {
            const percentChange = ((currentBalance - prevValue) / prevValue) * 100;
            gainValues[historyData.length - 1] = parseFloat(percentChange.toFixed(2));
          }
        }
      }
      
      // Find min and max values for better y-axis scaling
      const filteredGainValues = gainValues.filter(v => v !== null);
      const maxValue = Math.max(...filteredGainValues, 1); // At least 1% to avoid tiny scale
      const minValue = Math.min(...filteredGainValues, -1); // At least -1% to avoid tiny scale
      const valueRange = Math.max(Math.abs(maxValue), Math.abs(minValue));
      const suggestedMax = Math.ceil(valueRange * 1.1); // Add 10% padding
      const suggestedMin = -suggestedMax; // Keep the scale symmetric
      
      dailyGainChartInstance.current = new Chart(gainCtx, {
        type: 'bar',
        data: {
          labels: dates,
          datasets: [
            {
              label: 'Daily Gain %',
              data: gainValues,
              backgroundColor: gainValues.map(value => 
                value === null ? 'rgba(0,0,0,0)' :
                value >= 0 ? 'rgba(40, 167, 69, 0.6)' : 'rgba(220, 53, 69, 0.6)'
              ),
              borderColor: gainValues.map(value => 
                value === null ? 'rgba(0,0,0,0)' :
                value >= 0 ? 'rgb(40, 167, 69)' : 'rgb(220, 53, 69)'
              ),
              borderWidth: 1,
              barPercentage: 0.6, // Slightly narrower bars to prevent cutoff
              categoryPercentage: 0.7, // More space between bars
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              right: 15 // Add extra padding to the right
            }
          },
          plugins: {
            legend: {
              display: false, // Hide legend
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              backgroundColor: chartTheme === 'light' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(33, 37, 41, 0.95)',
              titleColor: chartTheme === 'light' ? '#212529' : '#f8f9fa',
              bodyColor: chartTheme === 'light' ? '#212529' : '#f8f9fa',
              borderColor: chartTheme === 'light' ? '#dee2e6' : '#495057',
              borderWidth: 1,
              callbacks: {
                label: (context) => {
                  const value = context.raw;
                  if (value === null) return null;
                  return `Daily Change: ${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
                }
              }
            },
            datalabels: {
              align: (context) => {
                return context.dataset.data[context.dataIndex] >= 0 ? 'end' : 'start';
              },
              anchor: (context) => {
                return context.dataset.data[context.dataIndex] >= 0 ? 'end' : 'start';
              },
              offset: 4,
              color: (context) => {
                if (context.dataset.data[context.dataIndex] === null) return 'rgba(0,0,0,0)';
                return context.dataset.data[context.dataIndex] >= 0 ? 
                  'rgb(40, 167, 69)' : 'rgb(220, 53, 69)';
              },
              font: {
                weight: 'bold',
                size: 11
              },
              formatter: (value) => {
                if (value === null) return '';
                return value >= 0 ? `+${value.toFixed(1)}%` : `${value.toFixed(1)}%`;
              },
              display: (context) => {
                return context.chart.data.labels.length <= 30 && context.dataset.data[context.dataIndex] !== null;
              }
            }
          },
          scales: {
            x: {
              ...commonXConfig,
              title: {
                display: true,
                text: 'Date',
                color: chartTheme === 'light' ? '#212529' : '#f8f9fa'
              },
              afterFit: function(scaleInstance) {
                scaleInstance.paddingRight = 25;
              }
            },
            y: {
              title: {
                display: true,
                text: 'Daily Gain (%)',
                color: chartTheme === 'light' ? '#212529' : '#f8f9fa'
              },
              ticks: {
                color: chartTheme === 'light' ? '#212529' : '#f8f9fa',
                callback: (value) => {
                  return value.toFixed(1) + '%';
                }
              },
              grid: {
                color: (context) => {
                  if (context.tick.value === 0) {
                    return chartTheme === 'light' ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.5)'; // Darker line for zero
                  }
                  return chartTheme === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)'; // Theme-aware grid lines
                },
                lineWidth: (context) => {
                  if (context.tick.value === 0) {
                    return 2; // Thicker line for zero
                  }
                  return 1;
                }
              },
              suggestedMin: suggestedMin,
              suggestedMax: suggestedMax,
              beginAtZero: false,
              afterDataLimits: (scale) => {
                if (scale.min > 0) scale.min = 0;
                if (scale.max < 0) scale.max = 0;
              }
            }
          }
        }
      });
    } catch (gainChartError) {
      logger.error('Error creating daily gain chart:', gainChartError);
      logger.warn('Daily gain chart failed to render but balance chart should still work');
    }
    
  }, [
    show,
    historyData,
    loading,
    isAggregated,
    currentBalance,
    chartTheme,
    allTimeHighs,
    combinedLoading
  ]);
  
  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      centered
      backdrop={true}
      keyboard={true}
      size="lg"
      dialogClassName="balance-history-modal"
    >
      <Modal.Header 
        style={{ 
          backgroundColor: chartTheme === 'light' ? '#f8f9fa' : '#374151',
          color: chartTheme === 'light' ? '#212529' : '#f8f9fa',
          borderBottomColor: chartTheme === 'light' ? '#dee2e6' : '#4b5563'
        }}
      >
        <Modal.Title className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center">
            <span>
              {isAggregated
                ? 'Total Portfolio'
                : `${accountName || 'Account'}${portfolioName ? ` (${portfolioName})` : ''}`
              }
              {!combinedLoading && historyData.length > 0 && allTimeHighs.length > 0 && allTimeHighs[allTimeHighs.length - 1] === historyData.length - 1 && (
                <span className="text-warning ms-2">⭐</span>
              )}
            </span>
            <div className="d-flex align-items-center ms-3">
              <small className="text-muted me-2">Period:</small>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="form-select form-select-sm me-3"
                style={{ 
                  fontSize: '0.75rem',
                  width: 'auto',
                  backgroundColor: chartTheme === 'light' ? '#ffffff' : '#374151',
                  color: chartTheme === 'light' ? '#212529' : '#f8f9fa',
                  borderColor: chartTheme === 'light' ? '#dee2e6' : '#4b5563'
                }}
              >
                <option value="ytd">Year to Date</option>
                <option value="1month">1 Month</option>
                <option value="3months">3 Months</option>
                <option value="6months">6 Months</option>
                <option value="1year">1 Year</option>
                <option value="3years">3 Years</option>
                <option value="alltime">All Time</option>
              </select>
              {/* Chart theme follows app theme; no manual override */}
            </div>
          </div>
          <div className="d-flex align-items-center">
            {/* All Time High Distance Display */}
            {!combinedLoading && historyData.length > 0 && (
              <div className="me-4 text-end">
                <div className="small text-muted mb-1">
                  All Time High Distance
                </div>
                <div className={`fw-bold ${allTimeHighDistance.percentage === 0 ? 'text-warning' : allTimeHighDistance.percentage > 0 ? 'text-success' : 'text-danger'}`}>
                  {allTimeHighDistance.percentage === 0 ? (
                    <span>$0.00 (0%)</span>
                  ) : (
                    <span>
                      {allTimeHighDistance.amount >= 0 ? '' : '-'}${Math.abs(allTimeHighDistance.amount).toLocaleString('en-US', { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })} ({allTimeHighDistance.percentage.toFixed(2)}%)
                    </span>
                  )}
                </div>
              </div>
            )}
            {/* Total Balance Display */}
            {!combinedLoading && historyData.length > 0 && (
              <div className="me-4 text-end">
                <div className="small text-muted mb-1">
                  Total Balance
                </div>
                <div className="fw-bold" style={{ color: chartTheme === 'light' ? '#1e40af' : '#93c5fd' }}>
                  ${(currentBalance !== null ? currentBalance : historyData[historyData.length - 1]?.total_value)?.toLocaleString('en-US', { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                  }) || '0.00'}
                </div>
              </div>
            )}
            {/* Total Gain/Loss Display */}
            {!combinedLoading && historyData.length >= 1 && (
              <div className="me-4 text-end">
                <div className="small text-muted mb-1">
                  Total Gain
                </div>
                <span className={`fw-bold ${periodGainLoss.amount >= 0 ? 'text-success' : 'text-danger'}`}>
                  {periodGainLoss.amount >= 0 ? '' : '-'}${Math.abs(periodGainLoss.amount).toLocaleString('en-US', { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                  })} ({periodGainLoss.percentage.toFixed(2)}%)
                </span>
              </div>
            )}
            {/* Today Gain/Loss Display */}
            {!combinedLoading && historyData.length >= 2 && (
              <div className="text-end">
                <div className="small text-muted mb-1">
                  Today Gain
                </div>
                <span className={`fw-bold ${todayGainLoss.amount >= 0 ? 'text-success' : 'text-danger'}`}>
                  {todayGainLoss.amount >= 0 ? '' : '-'}${Math.abs(todayGainLoss.amount).toLocaleString('en-US', { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                  })} ({todayGainLoss.percentage.toFixed(2)}%)
                </span>
              </div>
            )}
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ 
        backgroundColor: chartTheme === 'light' ? '#ffffff' : '#1f2937', 
        color: chartTheme === 'light' ? '#212529' : '#f8f9fa' 
      }}>
        {combinedLoading ? (
          <div className="text-center my-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <div className="mt-3 text-muted small">{loadingMessage}</div>
          </div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : historyData.length === 0 ? (
          <div className="alert alert-info">
            No balance history data available. Try saving account history first.
          </div>
        ) : (
          <div className="charts-container">
            {/* Balance Chart - TOP */}
            <div style={{ height: '35vh', position: 'relative', marginBottom: '0' }}>
              <canvas ref={balanceChartRef} />
            </div>
            
            {/* Dividing line between charts */}
            <div className="connector-line"></div>
            
            {/* Daily Gain Chart - BOTTOM */}
            <div style={{ height: '25vh', position: 'relative', marginTop: '0' }}>
              <canvas ref={dailyGainChartRef} />
            </div>
            
            <div className="text-muted mt-2 small text-center">
              <em>Note: Gaps in the chart represent days without recorded balance data.</em>
            </div>
          </div>
        )}
      </Modal.Body>
      <style jsx="true">{`
        .balance-history-modal {
          max-width: 90vw;
          color: #212529;
        }
        @media (min-width: 992px) {
          .balance-history-modal {
            max-width: 80vw;
          }
        }
        .connector-line {
          height: 2px;
          background-color: rgba(0,0,0,0.2);
          margin: 0;
          padding: 0;
        }
        .charts-container {
          display: flex;
          flex-direction: column;
          align-items: stretch;
        }
        /* Follows app theme; no overrides */
      `}</style>
    </Modal>
  );
}

export default BalanceHistoryModal; 
